import { useEffect, useState } from 'react';
import { formatMoney } from '@dastarhan/core';
import { BASE } from '../api';

const FLOATS = [20000_00, 40000_00, 60000_00];

export function ShiftGate({ cashier, token, onOpen, onContinue, onLogout, waiter }: {
  cashier: string;
  token: string;
  onOpen: (openingCash: number) => void;
  /** продолжить смену, уже открытую на этом терминале */
  onContinue: (shiftId: string) => void;
  waiter: boolean;
  onLogout: () => void;
}) {
  const [float, setFloat] = useState(40000_00);

  /**
   * Открытая смена на этом терминале.
   *
   * Спрашиваем ДО того, как предложить открыть новую: иначе человек
   * введёт размен, нажмёт «Открыть», а сервер подхватит чужую смену —
   * и деньги уйдут не туда.
   */
  const [prev, setPrev] = useState<{
    id: string; openedByName: string; openedAt: string;
    openingCash: number; checks: number; revenue: number;
  } | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    void (async () => {
      try {
        const r = await fetch(`${BASE}/shifts/current`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = await r.json() as { open: boolean } & Record<string, never>;
        setPrev(d.open ? (d as never) : null);
      } catch {
        // Без связи не мешаем работать: смена откроется, а сервер
        // разберётся с двойной при первой синхронизации
        setPrev(null);
      } finally {
        setChecking(false);
      }
    })();
  }, [token]);

  // Смена уже открыта — предлагаем выбор вместо молчаливого дубля
  if (!waiter && prev) {
    const mine = prev.openedByName === cashier;
    return (
      <div className="gate">
        <span className="eyebrow">{cashier}</span>
        <h1>Смена уже открыта</h1>

        <div className="prev-shift">
          <div className="ps-row"><span>Открыл</span><b>{prev.openedByName}</b></div>
          <div className="ps-row">
            <span>Когда</span>
            <b>{new Date(prev.openedAt).toLocaleString('ru-RU', {
              day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
            })}</b>
          </div>
          <div className="ps-row"><span>Размен</span><b>{formatMoney(prev.openingCash)}</b></div>
          <div className="ps-row"><span>Пробито чеков</span><b>{prev.checks}</b></div>
          <div className="ps-row"><span>Выручка</span><b>{formatMoney(prev.revenue)}</b></div>
        </div>

        {mine ? (
          <p className="hint">
            Это ваша смена — вы вернулись после перерыва. Продолжайте,
            заново открывать не нужно.
          </p>
        ) : (
          /* Чужую смену продолжать нельзя молча: её выручка и ящик
             принадлежат тому, кто её открыл. Либо он сам закрывает,
             либо новый кассир принимает её на себя осознанно */
          <p className="hint">
            Смену вёл другой кассир. Если он ушёл — примите её на себя:
            прежняя закроется, и дальше выручка пойдёт вам.
          </p>
        )}

        <div className="gate-actions">
          <button className="btn accent" onClick={() => onContinue(prev.id)}>
            {mine ? 'Продолжить смену' : 'Принять смену'}
          </button>
          <button className="btn ghost" onClick={onLogout}>Выйти</button>
        </div>
      </div>
    );
  }

  return (
    <div className="gate">
      <span className="eyebrow">{cashier} · {new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}</span>
      {checking && <p className="hint">Смотрим, нет ли открытой смены…</p>}
      {waiter && (
        <>
          <h1>Смена официанта</h1>
          <p className="hint">Деньги не считаем: заказы уходят на кассу, там и оплата.</p>
          <button className="btn accent" style={{ width: 'min(360px, 92vw)' }} onClick={() => onOpen(0)}>
            Начать смену
          </button>
          <button className="btn ghost" onClick={onLogout}>Сменить сотрудника</button>
        </>
      )}
      {!waiter && (<>
      <h1>Сколько наличных в ящике?</h1>
      <p className="hint">Размен на сдачу. Вечером касса сверит ящик с этой суммой сама.</p>
      <div className="quick" style={{ width: 'min(360px, 92vw)' }}>
        {FLOATS.map((v) => (
          <button key={v} className={`btn ${float === v ? 'accent' : ''}`} onClick={() => setFloat(v)}>
            <span className="money">{formatMoney(v)}</span>
          </button>
        ))}
        <button className={`btn ${float === 0 ? 'accent' : ''}`} onClick={() => setFloat(0)}>Пусто</button>
      </div>
      <button className="btn accent" style={{ width: 'min(360px, 92vw)' }} onClick={() => onOpen(float)}>
        Открыть смену · <span className="money">{formatMoney(float)}</span>
      </button>
      <button className="btn ghost" onClick={onLogout}>Сменить кассира</button>
      </>)}
    </div>
  );
}
